import GameManager from '../../cvp-webgames-core/GameManager';
import BaseScenarioPart from '../../cvp-webgames-core/Scenario/BaseScenarioPart';
import { VideoEnvironmentProps } from '../../cvp-webgames-core/Components/Environments/React/VideoEnvironment';
import { DaarcBaseScenario } from '../DaarcBaseScenario';
import BasePracticeScenario from '../base-practice-scenario/BasePracticeScenario';
import { constants } from '../../misc/constants';
import { DebriefIntro, DebriefVortex } from '../../components/ui/DebriefUI';
import { AssessAirwayActionData, AssessAirwayActions } from '../../components/ui/AssessAirwayUI';
import { SuccessType } from '../general/general-scenario-stuff';
import { Windows } from '../../misc/tools';
import { VortexUICurrentAttempt } from '../../components/ui/hud/VortexUI';
import BaseTestScenario from '../base-test-scenario/BaseTestScenario';

export default class FaithAlvarado extends BaseTestScenario {
	public sGACorrect: boolean;
	public get maxScore(): number {
		return 54600;
	}

	public get predeterminedPoints(): number {
		var assessPatientRemoveDenturesPoints = 500; //Patient dentures are already removed
		var setupFiberOpticsPoints = 1000;
		var cricPoints = 3900;
		var correctAttempt = 32700;


		return assessPatientRemoveDenturesPoints +
			setupFiberOpticsPoints + 
			cricPoints +
			correctAttempt;
	}

	public get totalNumberOfQuestions(): number {
		return 5;
	}

	public get currentLevel(): number {
		return 8;
	}

	public showDefaultSideMenu() {
		const context = this;
		context.sideMenuId = context.uiWrapper.displaySideMenu({
			gameManager: this.gameManager,
			inductionTimeInSeconds: 30,
			apneaTimeInSeconds: 210,
			bloodPressureNumerator: 150,
			bloodPressureDenominator: 90,
			bloodPressureMinNumerator: 50,
			bloodPressureMinDenominator: 33,
			bloodPressureMaxNumerator: 220,
			bloodPressureMaxDenominator: 110,
			sa02: 91,
			heartRate: 110,
			heartRateMax: 150,
			respirationRate: 28,
			resetEtc02: false,
			etc02: 35,
			showETC02: false,
			isApneaTimerIdle: true,
			onInductionTimerRanOut: () => {
				context.hasInductionTimerRanOut = true;
			},
			onApneaTimerRanOut: () => {
                context.failedScenario = true;
				this._CheckOxy();
				context.onShowSuccessFailedAttempt(constants.attemptText.failedTitle, 'You did not establish a stable, patent airway in time to save Faith Alvarado’s life. She has expired. Review the debrief and then try the scenario again.', true);
			}
		}, context.sideMenuId);
	}

	constructor(gameManager: GameManager, scenarioName: string) {
		super(gameManager, scenarioName);

		this._setupIntro();
		this._setupGeneralNeutralState();
		this._setupProcedures();
	}

	public getDebriefIntro(): DebriefIntro {
		return {
			items: [
				'Ms. Alvardo’s thyroid tumor should indicate that she may have airway distortion and be difficult to intubate. As a result, you should call for a fiberoptic laryngoscope early.',
				'Ms. Alvardo’s thyroid tumor has distorted her trachea. Surgical landmarks for a cricothyrotomy aren’t visible or palpable. In these cases, it’s unwise to attempt a cricothyrotomy.You cannot see the pharynx when you use the laryngoscope, which indicates that the patient can’t be intubated. If you attempt to pass the tube with this view, you may have an esophageal intubation.',
				'Did you factor in the clinical condition of the patient in your utilization of the Vortex approach?'
			],
			link: 'https://google.com'
		};
	}

	public getDebriefVortex(): DebriefVortex {
		return {
			successItems: [
				'BVM: ' + this.bvmAttemptCount + ' attempt(s)',
				'ETT: ' + this.ettAttemptCount + ' attempt(s)',
				'SGA: ' + this.sgaAttemptCount + ' attempt(s)',
				'Cricothyrotomy: ' + this.cricAttemptCount + ' attempt(s)'
			],
			askYourselfItems: [
				'Did you exceed the maximum number of optimizations (3) for a given attempt?',
				'Did you optimize between each attempt at the bag valve mask, intubation and supraglottic airway?',
				'Did you follow the Vortex approach by not returning to a technique that you had already attempted with appropriate optimizations and failed?',
				'How can you more efficiently manage the airway? Remember that the quicker you establish a stable, patent airway, the better the patient outcome.'
			]
		};
	}

	public onBVMFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.BVM, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onETTFinished(): void {
		this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.ETT, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt');
	}

	public onSGAFinished(): void {
		if(this.sGACorrect){
			this.addScore(this.predeterminedPoints);
			this._CheckOxy();
			this.showSuccessPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'You were able to get Faith Alverado into the green zone by placing a size 3 supraglottic airway (SGA).  Ms. Alverado was apneic for ' + this.apneaTimeElapsed + ' while you managed her airway. You kept Ms. Alverado in the green zone until the team with the flexible bronchoscope arrived. The team then established a stable, patent airway for her. You earned ' + this.totalScore + ' out of a possible ' + this.maxScore + ' points. Review the debrief for a summary of key learning points.', 'Good Job on this Scenario'); 
		} else {
			this.showFailedAttemptPopUpAfterAuscultate(VortexUICurrentAttempt.SGA, 'A patent airway has not been achieved. Continue attempts at managing the airway.', 'Failed Attempt')
		}
	}

	public onCricFinished(): void {
		this.showFailedScenarioPopUpAfterAuscultate(VortexUICurrentAttempt.Cric);	// PATIENT SHOULD EXPIRE IF ATTEMPTED, setup in cric config2	
	}

	public _CheckOxy() {
		if(!this.preOxDone) {
			this.debriefLogFailedItem("Pre-Oxygenation: You did not pre-oxygenate the patient.")
		};
		if(!this.callFlexBr) {
			this.debriefLogFailedItem("Call for flexible bronchoscope: Recognizing the need for a flexible bronchoscope early is critical, especially when the patient’s condition indicates intubation and use of a supraglottic airway will be anatomically impossible.")
		}			
	}

	public getAssessAirwayData(): AssessAirwayActionData[] {
		return [
			{
				relatedAction: AssessAirwayActions.ExamineNeck,
				title: 'Examine Neck',
				text: 'Trachea is immobile. The thyroid tumor on the left deviates the trachea to the right.'
			},
			{
				relatedAction: AssessAirwayActions.LiftChinToCeiling,
				title: 'Lift chin to ceiling',
				text: 'Thyromental Distance: 4 finger breadths (6 cm) but distorted by thyroid tumor<br />Hyoid to Thyroid Notch Distance: 2 finger breadths (3 cm)'
			},
			{
				relatedAction: AssessAirwayActions.PutChinToChest,
				title: 'Put chin to chest',
				text: 'You see the patient moving her chin to her chest.'
			},
			{
				relatedAction: AssessAirwayActions.RemoveDentures,
				title: 'Remove Dentures',
				text: 'The patient is not wearing her dentures.',
				noPoints: true
			},
			{
				relatedAction: AssessAirwayActions.ShowTeeth,
				title: 'Smile for me',
				text: 'No, my dentures are out.'
			},
			{
				relatedAction: AssessAirwayActions.StickOutTongue,
				imgUrl: './assets/images/assess-airway-images/Class2NoTeeth.png',
				title: 'Open mouth',
				text: 'Inter-incisor Distance: 3 finger breadths (4 cm)'
			}
		]
	}

	private _setupIntro() {
		// this.intro.config.part1Config = {
		// 	videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
		// 	dialogs: [
		// 		{
		// 			speakerName: constants.speakers.RnMiranda,
		// 			text: `
		// 			We have Faith Alvarado here. She’s a 70-year-old female, 
		// 			68 inches, 60 kg. She has a large left thyroid tumor, 
		// 			which is deviating the trachea to the right, hypertension, 
		// 			and pulmonary fibrosis. She’s on home oxygen. She came in 
		// 			with pneumonia. She hasn't eaten in 8 hours. K is 3.6. She’s 
		// 			dyspneic. Her respiratory rate is 28.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario7 - intro - 53'
		// 		},
		// 		{
		// 			speakerName: constants.speakers.DrCaleb,
		// 			text: `
		// 				I’m already administering epinephrine and steroids. I’ll manage the
		// 				hemodynamics; you manage the airway.
		// 			`,
		// 			VideoChapter: () =>'DAARC - Scenario7 - intro - 53'
		// 		}
		// 	],
		// 	sbarConfig: {
		// 		s: `This patient has a presumptive pneumonia.`,
		// 		b: `
		// 			Age: 70<br />
		// 			Height (in): 68 in<br />
		// 			Weight (kg): 60 kg<br />
		// 			Potassium (K): 3.6<br />
		// 			NPO Status: Ate more than 8 hours ago.<br />
		// 			<br />
		// 			PMH: Has a large thyroid tumor, hypertension, pulmonary fibrosis on home oxygen.
		// 		`,
		// 		a: `Patient has a presumptive pneumonia in the setting of a large thyroid tumor that causes tracheal deviation.`,
		// 		r: `She needs a stable, patent airway to assist with her long-term care.`
		// 	}
		// }

		this._id_ScenarioSet(7); // <<<<<< CRITICAL: must be set in every scenario. This number does not always match the "level number" variable set in the declarations above, use the documented level numbers and not how they are listed in game <<<<<<<<

		this.intro.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			chapters: [
				{
					chapterName: () => 'DAARC - Scenario7 - intro - 53'
				}
			],
			sbarConfig: {
				s: `This patient has a presumptive pneumonia.`,
				b: `
					Age: 70<br />
					Height (in): 68 in<br />
					Weight (kg): 60 kg<br />
					Potassium (K): 3.6<br />
					NPO Status: Ate more than 8 hours ago.<br />
					<br />
					PMH: Has a large thyroid tumor, hypertension, pulmonary fibrosis on home oxygen.
				`,
				b508: `
					Age. 70.
					Height. 68 inches.
					Weight. 60 kilograms.
					Potassium. 3.6.
					NPO Status. Ate more than 8 hours ago.
					
					Past Medical History. Has a large thyroid tumor, hypertension, pulmonary fibrosis on home oxygen.
				`,
				a: `Patient has a presumptive pneumonia in the setting of a large thyroid tumor that causes tracheal deviation.`,
				r: `She needs a stable, patent airway to assist with her long-term care.`
			}
		}
	}

	private _setupGeneralNeutralState() {
		this.neutralState.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			hudIdleVideoChapter: () =>'DAARC - Scenario7 - Flat - Nothing - 1',
			onCalledForVortex: () => {
				this.debriefLog('Called for a Vortex reader');
			}
		};

		var toolsArr = null
		if(this.isLite) {
			toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,	
				//constants.toolbagTools.cric //comment this out on cric not required scenarios		
			]
		} else {			
				toolsArr = [
				constants.toolbagTools.bvm,
				constants.toolbagTools.ett,
				constants.toolbagTools.sga,
				constants.toolbagTools.cric
			]
		}

		this.neutralState.config.part2Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			toolsToChooseFrom: toolsArr,
			onTeamNotDebriefed: () => {
				this.debriefLogFailedItem("Brief other staff in the room about attempt order: Briefing your staff on the attempt order will help them anticipate providing the right equipment. Briefings empower your staff to notify you if you fall into fixation error. ")
			},
			onTeamDebriefed: () => {
				this.debriefLog('Briefed other staff in the room about attempt order');
			},
			onToolsOrganized: () => {
				this.debriefLog('Organized equipment');
			}
		};

		this.neutralState.config.part3Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			waitForSuctionVideoChapter: () =>'DAARC - Scenario7 - Flat - Nothing - 1',
			onSuctionSet: () => {
				this.debriefLog("Suction set");
			}
		};

		this.neutralState.config.part4Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			waitForAdjustBedHeightVideoChapter: () =>'DAARC - Scenario7 - 45 - Nothing - 7',
			onAdjustedBedHeight: () => {
				this._id_bedPos = this._id_bedAdj; // <<<<<<<
				this.debriefLog("Bed Adjusted");
			}
		};

		this.neutralState.config.part5Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			liftChinVideoChapter: () =>'DAARC - Scenario7 - 45 - patient-assessment-up - 36',
			chinToChestVideoChapter: () =>'DAARC - Scenario7 - 45 - patient-assessment-down - 37',
			examineNeckVideoChapter: () =>'DAARC - Scenario7 - 45 - patient-assessment-straight - 35',
			realTeethVideoChapter: () =>'DAARC - Scenario7 - 45 - patient-assessment-straight - 35',
			stickOutTongueVideoChapter: () =>'DAARC - Scenario7 - 45 - patient-assessment-straight - 35',
			onAssessmentFinied: () => {
				this.debriefLog("Performed a complete airway assessment")
			}
		};

		this.neutralState.config.part6Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			waitForFiberOpticVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText),
			onSetupFiberOptic: () => {
				this.callFlexBr = true;
				this.debriefLog("Called for advanced airway cart")
			}
		};

		this.neutralState.config.part7Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			waitForPreOxygenateVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // this needs to update based on the selection made		
			onPreOxygenated: (selection: number) => {
				this.preOxDone = true;
				this._id_UpdatePreOxText(selection); // this should update the pre-oxygenation neutral video segment, but the reference set above might not reflect this update
				switch(selection) {
					case 0:
						this.debriefLog("Pre-Oxygenated with High Flow Nasal Cannula");
						break;
					case 1:
						this.debriefLog("Pre-Oxygenated with Nasal Noninvasive Positive Pressure");
						break;
					case 2:
						this.debriefLog("Pre-Oxygenated with 100% Non-Rebreather Mask");
						break;
					case 3:
						this.debriefLogFailedItem("Pre-Oxygenated with BVM. This may not be the best solution");
						break;
				}
			}
		};

		this.neutralState.config.part8Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			onApneaStartedVideoChapter: () => this._id_SegmentAssembly(this._id_preOxText), // APNEA TIMER INPUT SEGMENT CHANGE HERE,
			meds: [
				{
					title: 'Induction Agent',
					audioPrefix: 'InductionAgent_'
				},
				{
					title: 'Administer Neuromuscular Junction Blocker',
					audioPrefix: 'NMJB_'
				}
			],
			onPatientFullyMedicated: () => {	
				this.debriefSetAdministeringText("You correctly medicated your patient and earned maximum points.")
			},
			onSingleMedAdministered: () => {
				this.debriefSetAdministeringText("Did you administer all medications that are appropriate, given the patient's condition?") //May need more description			
			}
		};
	}

	private _setupProcedures() {
		this._setupBvm();
		this._setupEtt();
		this._setupSga();
		this._setupCric();
		this._setupAuscultate();
	}

	public getDefaultSa02() {
		return 91
	}

	public getDefaultApneaTime() {
		return 210;
	}

	private _setupBvm() {
		this.procedures.bvm.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			chooseHandVideoChapter: () => this._id_bvmHandAttempt,
			onChooseHandNextClicked: (isOneHanded: boolean) => {
				if(isOneHanded) {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(1)) // set segment variable equal to this
				} else {
					this._id_bvmHandAttempt = this._id_SegmentAssembly(this._id_BVMAttempt(2)) // set segment variable equal to this
				}
			}
		};
		this.procedures.bvm.config.part2Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			onProcedureAttempted: (attemptCount: number) => {
				this.bvmAttemptCount = attemptCount
			}
		}
	}

	private _setupEtt() {
		this.procedures.ett.config.ettResistanceConfig = { 
			show: () => true,
			onNextClicked: () => {
			}
		}
		this.procedures.ett.config.part1Config = {
			hideVlButton: () => true,
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			insertingETTVideoChapter: () => this._id_SegmentAssembly(this._id_ETTAttempt()),			
			ettSpecialUIConfig: {
				airwayImage: () => {
					return './assets/images/ett/Grade1Tumor.png';
				},
				magillForceps: {
					text: () => 'There is no foreign body in the patient’s airway.'
				},
				suction: {
					text: () => ''
				},
				manipulateLarynx: {
					text: () => ''
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.ettAttemptCount = attemptCount
			}
		};
	}

	private _setupSga() {
		this.procedures.sga.config.isAttemptSuccessful = () => {
			return this.sGACorrect;
		}
		this.procedures.sga.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			squeezeAmbuBagVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			insertingSgaVideoChapter: () =>this._id_SegmentAssembly(this._id_SGAAttempt()),
			onMakeAttemptClicked:(selectedSGA: string) => {
				if(selectedSGA === '3') {
					this.sGACorrect = true;

				} else {
					this.sGACorrect = false;
				}
			},
			onProcedureAttempted: (attemptCount: number) => {
				this.sgaAttemptCount = attemptCount
			}						
		};
	}

	private _setupCric() {
		const context = this;
		this.procedures.cric.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			correctToolOrder: [
				'Scalpel',
				'Spreader',
				'Bougie',
				'ETT',
				'Syringe',
			],		
		};
		this.procedures.cric.config.part2Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			identifyLandmarksVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(1), 'flat'),
			areLandmarksObstructed: true,
			landmarksObstructedMessageText: 'Unable to identify landmarks due to the large left thyroid tumor obstruction.',
			landmarksObstructedMessageTitle: 'Landmark Identification Obstructed'
		};
		this.procedures.cric.config.part3Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			
		};
		this.procedures.cric.config.part4Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			scalpelVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(2), 'flat')
,			onMakingIncition: () => {
				this.cricAttemptCount++;
				this._CheckOxy();
				context.showFailedScenarioPopUp('Due to the thyroid tumor distortion of the trachea, the surgical landmarks were not visible or palpable. In these cases, it’s unwise to attempt a cricothyrotomy. Additionally, your incision of the thyroid tumor caused a massive hemorrhage. As a result, Faith Alvarado expired. Review the debrief and then try again.') 
			}	
		};
		this.procedures.cric.config.part5Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			spreaderVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(3), 'flat')
		};
		this.procedures.cric.config.part6Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			bougieVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(4), 'flat')
		};
		this.procedures.cric.config.part7Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			etTubeVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(5), 'flat')
		};
		this.procedures.cric.config.part8Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			squeezeAmbuBagVideoChapter: () => this._id_SegmentAssembly(this._id_CricPart(6), 'Flat')
		};
	}

	private _setupAuscultate() {
		this.procedures.auscultate.config.part1Config = {
			videoUrl: './assets/videos/scenario7/daarc-scenario7-faithalvarado-v1.mp4',
			bvmConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_bvmAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}				
			},
			ettConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_ettAusc),
				checkRiseAndTubeHumidificationText: () => this.auscFailed,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return !isYesClicked
				},				
				auscultateHoverOverMessages: () => {
					return this.hoverBreathNo;
				}
			},
			sgaConfig: {
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_sgaAusc),
				checkRiseAndTubeHumidificationText: () => {
					if(this.sGACorrect) {
						return this.auscSuccess
					} else {
						return this.auscFailed
					}
				},
				audioPath: () => {
					if(this.sGACorrect) {
						return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
					} else {
						return './assets/audio/auscultate/vortexreaderreminder_fail.mp3';
					}
					
				},				
				isAnswerCorrect: (isYesClicked: boolean) => {
					if(this.sGACorrect) {
						return isYesClicked;
					} else return !isYesClicked
				},
				auscultateHoverOverMessages: () => {
					if(this.sGACorrect) {
						return this.hoverBreathYes
					} else {
						return this.hoverBreathNo
					}					
				}
			},
			cricConfig: { // Patient will expire if the cric is attempted, so this section will not be reachable
				moveToAuscultateScreenVideoChapter: () => this._id_SegmentAssembly(this._id_cricAusc, 'flat'),
				checkRiseAndTubeHumidificationText: () => this.auscSuccess,
				audioPath: () => {
					return './assets/audio/auscultate/vortexreaderreminder_success2.mp3';
				},
				isAnswerCorrect: (isYesClicked: boolean) => {
					return isYesClicked;
				},
				auscultateHoverOverMessages: () => {
					return this.hoverBreathYes;
				}
			}
		}
	}


	public getParts(): BaseScenarioPart<DaarcBaseScenario>[] {
		return this.parts;
	}

	public getSoundPath(fileName: string): string {
		return './assets/audio/scenario1/' + fileName + '.ogg';
	}
}